/**
 * localDemoOps.ts — Helpers compartidos para operaciones offline / demo en localStorage.
 *
 * QUÉ HACE: Centraliza cuándo preferir LS sobre API y detectar errores recuperables.
 * QUÉ EXPORTA: shouldPreferLocalDemoOps, shouldFallbackToLocalStorage
 * QUÉ NO DEBE HACER: Lógica de dominio ni UI.
 */

import { txReadRaw, txWriteRaw } from "@/lib/persistence/transactionalStore";
import { isDeviceOnline, shouldPreferLocalCajaOps } from "@/lib/networkStatus";
import { isCajaRemoteApiEnabled } from "@/features/caja/utils/cajaSessionLocal";

export const LS_REAL_ACTIVITY = "altenot_real_activity_started";

/**
 * Verifica si el usuario ha realizado alguna acción real en el sistema
 * que invalide los datos demo/mock iniciales.
 */
export function hasRealActivityStarted(): boolean {
  try {
    // Solo verifica el flag genérico de actividad real (auditoría, inventario, etc.).
    // altenot_caja_user_real_pos es exclusivo de Caja y se verifica por separado.
    return txReadRaw(LS_REAL_ACTIVITY) === "1";
  } catch {
    return false;
  }
}

/**
 * Marca que el usuario ha iniciado actividad real.
 */
export function markRealActivityStarted(): void {
  try {
    txWriteRaw(LS_REAL_ACTIVITY, "1");
    // NOTA: NO propagamos a altenot_caja_user_real_pos porque esa clave es
    // exclusiva de actividad real en el POS de Caja. Marcar auditoría, inventario
    // u otro módulo NO debe ocultar los datos demo de Caja.
    window.dispatchEvent(new CustomEvent("altenot-real-activity-started"));
  } catch {
    /* ignore */
  }
}

/** Demo/APK sin API remota: operaciones van directo a localStorage. */
export function shouldPreferLocalDemoOps(): boolean {
  if (shouldPreferLocalCajaOps()) return true;
  try {
    return txReadRaw("altenot_demo_mode") === "true";
  } catch {
    return false;
  }
}

function isRecoverableLocalError(error: unknown): boolean {
  const status = (error as { response?: { status?: number } })?.response
    ?.status;
  return status === undefined || status === 404 || status === 502;
}

/** Offline o error de red/404 cuando no hay API remota configurada. */
export function shouldFallbackToLocalStorage(error?: unknown): boolean {
  if (shouldPreferLocalDemoOps()) return true;
  if (!isDeviceOnline() && !isCajaRemoteApiEnabled()) return true;
  if (error !== undefined) return isRecoverableLocalError(error);
  return false;
}
